// apiClient.js
import axios from 'axios';
import { getIdJwtToken } from '../utils/cognitoTokens';
import { config } from '../config';

const apiClient = axios.create();
export { ACTIVE_ACCOUNT_STORAGE_KEY, ACTIVE_ACCOUNT_HEADER } from './accountSelection';
import { ACTIVE_ACCOUNT_STORAGE_KEY, ACTIVE_ACCOUNT_HEADER } from './accountSelection';

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;
let redirectTimestamp = null;
const REDIRECT_TIMEOUT = 5000; // Auto-reset after 5 seconds

// Debug mode - set to true to enable detailed logging
const DEBUG_INTERCEPTORS = false;

// Helper to check and auto-reset redirect flag
const checkRedirectFlag = () => {
    if (isRedirecting && redirectTimestamp) {
        const elapsed = Date.now() - redirectTimestamp;
        if (elapsed > REDIRECT_TIMEOUT) {
            console.warn('[apiClient] Auto-resetting isRedirecting flag after timeout');
            isRedirecting = false;
            redirectTimestamp = null;
        }
    }
    return isRedirecting;
};

// Helper to set redirect flag
const setRedirectFlag = () => {
    isRedirecting = true;
    redirectTimestamp = Date.now();
};

const getAuthErrorMessage = (error) => {
    if (typeof error === 'string') {
        return error;
    }

    return error?.message || '';
};

const isNoCurrentUserError = (error) => {
    return /^no current user\.?$/i.test(getAuthErrorMessage(error).trim());
};

// External service endpoints that shouldn't trigger auth failure logout
const EXTERNAL_SERVICE_ENDPOINTS = [
    '/slack-send-message',
    '/google/',
    '/configure/source/gong',
    '/webhook'
];

// Permission-based endpoints where 403 means "no permission", not "session expired"
const PERMISSION_BASED_ENDPOINTS = [
    "/planned-roster",
    "/compensation",
    "/company/run-post-config",
    "/configure/run-hscrm-data-puller",
    "/configure/run-sfdc-data-puller",
    "/generate-predictions/all",
    "/generator/orchestrate",
    "/salesloft/trigger-puller",
    "/business-digest-weekly",
    "/notifications/inbound-response",
    "/notifications/missing-cost-notification",
    "/notifications/missing-quota",
    "/notifications/program-summary-notification",
    "/notifications/sales-pipeline-notification",
    "/notifications/stale-forecast",
    "/notify-errors/auth",
    "/stale-signup-notification",
    "/view-source-notification",
    "/forecast-reminder-orchestrator",
    "/notification-orchestrator",
    "/scenario-snapshot-orchestrator",

    '/recipes/folders',  // Folder API - 403 is permission denial, not auth failure
    '/recipes',          // All recipe operations (including /recipes with no trailing slash)
    '/subscriptions',    // Subscription ownership/admin checks may return 403
    '/organization-skills', // Organization membership/admin publication checks
    '/sales-ramp/user-start-dates',
    '/configure/assistant', // Feature-gated assistant endpoints may intentionally return 403.
    '/salesloft/oauth/init', // Integration/account failures must not invalidate the QFlow session.
    '/xero/authorize',       // Xero-side failures (state, exchange, org resolution) are not QFlow auth failures.
    '/xero/oauthcallback',
    '/zoho-analytics',       // Administrator/source access failures do not invalidate the QFlow session.
];

const ACCOUNT_HEADER_EXCLUDED_ENDPOINTS = [
    '/authentication/check-user',
    '/desktop/downloads',
];

const getRequestPath = (url = '') => {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return String(url || '');
    }
};

const getRoutePath = (url = '') => {
    return getRequestPath(url).replace(/^\/(dev|test|prod)(?=\/)/, '');
};

const getBrowserOrigin = () => {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return 'http://localhost';
};

const parseRequestUrl = (url = '', baseURL) => {
    try {
        return new URL(url, baseURL || getBrowserOrigin());
    } catch {
        return null;
    }
};

export const isQFlowBackendRequest = ({ url = '', baseURL } = {}) => {
    const backendUrl = parseRequestUrl(config.REACT_APP_BACKEND_HOST);
    const requestUrl = parseRequestUrl(url, baseURL);

    if (!backendUrl || !requestUrl) {
        return false;
    }

    const backendPath = backendUrl.pathname.replace(/\/$/, '');
    const isBackendOrigin = requestUrl.origin === backendUrl.origin;

    if (!isBackendOrigin) {
        return false;
    }

    return !backendPath
        || requestUrl.pathname === backendPath
        || requestUrl.pathname.startsWith(`${backendPath}/`);
};

const getActiveAccountOverrideId = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    return window.localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY) || null;
};

const hasHeader = (headers, headerName) => {
    if (!headers) {
        return false;
    }

    if (typeof headers.get === 'function') {
        return !!headers.get(headerName);
    }

    return Object.keys(headers).some((key) => key.toLowerCase() === headerName.toLowerCase());
};

const setHeader = (headers, headerName, value) => {
    if (typeof headers.set === 'function') {
        headers.set(headerName, value);
        return;
    }

    headers[headerName] = value;
};

const isAccountHeaderExcludedEndpoint = ({ url = '' } = {}) => {
    const requestPath = getRoutePath(url);

    return ACCOUNT_HEADER_EXCLUDED_ENDPOINTS.some((endpoint) => (
        requestPath === endpoint || requestPath.startsWith(`${endpoint}/`)
    ));
};

export const attachActiveAccountHeader = (requestConfig = {}) => {
    const activeAccountOverrideId = getActiveAccountOverrideId();

    if (
        requestConfig.qflowExecutionScope === 'all'
        || !activeAccountOverrideId
        || !isQFlowBackendRequest(requestConfig)
        || isAccountHeaderExcludedEndpoint(requestConfig)
    ) {
        return requestConfig;
    }

    requestConfig.headers = requestConfig.headers || {};

    if (!hasHeader(requestConfig.headers, ACTIVE_ACCOUNT_HEADER)) {
        setHeader(requestConfig.headers, ACTIVE_ACCOUNT_HEADER, activeAccountOverrideId);
    }

    return requestConfig;
};

const isPermissionBasedEndpoint = (url = '') => {
    const requestPath = getRoutePath(url);

    if (/^\/board-stats\/revenue-report\/[^/]+\/export$/.test(requestPath)) return true;

    return PERMISSION_BASED_ENDPOINTS.some((endpoint) => {
        // Match /recipes and any nested recipe routes, but not unrelated paths like /recipes-metadata
        if (endpoint === '/recipes') {
            return /\/recipes(?:\/|$)/.test(requestPath);
        }
        return requestPath.includes(endpoint);
    });
};

// Helper function for exponential backoff delay
const getRetryDelay = (retryCount, retryAfter = null) => {
    if (retryAfter) {
        // If server provides Retry-After header, use it (convert to milliseconds)
        const retryAfterMs = parseInt(retryAfter) * 1000;
        return Math.min(retryAfterMs, 30000); // Cap at 30 seconds
    }
    // Exponential backoff: 1s, 2s, 4s with jitter
    const baseDelay = Math.pow(2, retryCount) * 1000;
    const jitter = Math.random() * 0.1 * baseDelay; // 10% jitter
    return Math.min(baseDelay + jitter, 30000); // Cap at 30 seconds
};

// Sleep function for delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Raw axios calls still go through the default singleton, so attach the
// selected account header there too. apiClient has its own auth interceptor below.
axios.interceptors.request.use((requestConfig) => attachActiveAccountHeader(requestConfig));

// Add a request interceptor
apiClient.interceptors.request.use(
    async (config) => {
        if (DEBUG_INTERCEPTORS) {
            console.log('[apiClient] Request interceptor fired for:', config.url);
        }

        try {
            // Retrieve the token locally; no network request unless token is expired
            if (DEBUG_INTERCEPTORS) {
                console.log('[apiClient] Attempting to get current session');
            }

            const token = await getIdJwtToken();

            if (DEBUG_INTERCEPTORS) {
                console.log('[apiClient] Token retrieved successfully, length:', token?.length);
            }

            config.headers = config.headers || {};
            config.headers['Authorization'] = token;
            attachActiveAccountHeader(config);

            // Prevent browser caching by adding timestamp to GET requests
            // This prevents stale error responses (like 410 Gone) from being served from disk cache
            if (config.method === 'get' || config.method === 'GET') {
                if (!config.params) {
                    config.params = {};
                }
                config.params._t = Date.now();
            }

            // Reset redirect flag on successful token retrieval
            if (isRedirecting) {
                if (DEBUG_INTERCEPTORS) {
                    console.log('[apiClient] Resetting redirect flag on successful auth');
                }
                isRedirecting = false;
                redirectTimestamp = null;
            }

            return config;
        } catch (error) {
            console.error('[apiClient] Request interceptor error:', {
                code: error.code,
                message: error.message,
                name: error.name
            });
            console.error('[apiClient] Full error object:', error);
            console.error('[apiClient] Error type:', typeof error);
            console.error('[apiClient] Error keys:', error ? Object.keys(error) : 'null/undefined');

            // Handle token refresh errors (e.g., NotAuthorizedException, token revoked)
            if (error.code === 'NotAuthorizedException' ||
                error.message?.includes('Access Token has been revoked') ||
                isNoCurrentUserError(error)) {

                // Check redirect flag with auto-reset
                if (!checkRedirectFlag()) {
                    setRedirectFlag();
                    console.error('[apiClient] Authentication token is invalid or revoked:', error.message);

                    // Dispatch event to let App.js handle the auth failure
                    window.dispatchEvent(new CustomEvent('auth-failed', {
                        detail: {
                            reason: isNoCurrentUserError(error) ? 'no_current_user' : 'token_revoked',
                            message: getAuthErrorMessage(error)
                        }
                    }));

                    if (DEBUG_INTERCEPTORS) {
                        console.log('[apiClient] Dispatched auth-failed event');
                    }
                } else {
                    if (DEBUG_INTERCEPTORS) {
                        console.log('[apiClient] Redirect already in progress, skipping event dispatch');
                    }
                }
                // Reject the promise to stop the request
                return Promise.reject(error);
            }
            // Re-throw other errors
            return Promise.reject(error);
        }
    },
    (error) => {
        console.error('[apiClient] Request interceptor rejected with error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        if (DEBUG_INTERCEPTORS) {
            console.log('[apiClient] Response interceptor - success:', response.config?.url);
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (DEBUG_INTERCEPTORS) {
            console.log('[apiClient] Response interceptor - error:', {
                status: error.response?.status,
                url: originalRequest?.url,
                redirecting: checkRedirectFlag()
            });
        }

        // Handle 401 Unauthorized - token might be revoked or expired
        if (error.response?.status === 401) {
            // Check if this is an external service endpoint
            const isExternalService = EXTERNAL_SERVICE_ENDPOINTS.some(endpoint =>
                originalRequest.url?.includes(endpoint)
            );

            if (!isExternalService && !checkRedirectFlag()) {
                setRedirectFlag();
                console.error('[apiClient] 401 Unauthorized - signing out');

                // Dispatch event to let App.js handle the auth failure
                window.dispatchEvent(new CustomEvent('auth-failed', {
                    detail: {
                        reason: 'unauthorized',
                        status: 401
                    }
                }));

                if (DEBUG_INTERCEPTORS) {
                    console.log('[apiClient] Dispatched auth-failed event for 401');
                }
            } else if (isExternalService) {
                console.warn(`[apiClient] External service authentication failed for ${originalRequest.url}, not logging out user`);
            } else if (DEBUG_INTERCEPTORS) {
                console.log('[apiClient] 401 received but redirect already in progress');
            }

            return Promise.reject(error);
        }
        
        // Handle 403 Forbidden - session expired or access denied
        if (error.response?.status === 403) {
            // Check if this is a permission-based endpoint (not auth failure)
            const isPermissionEndpoint = isPermissionBasedEndpoint(originalRequest.url);

            // Check if this is an external service endpoint
            const isExternalService = EXTERNAL_SERVICE_ENDPOINTS.some(endpoint =>
                originalRequest.url?.includes(endpoint)
            );

            // Only trigger logout for auth failures, not permission denials
            if (!isPermissionEndpoint && !isExternalService && !checkRedirectFlag()) {
                setRedirectFlag();
                console.error('[apiClient] 403 Forbidden - session may have expired');

                // Dispatch event to let App.js handle the auth failure
                window.dispatchEvent(new CustomEvent('auth-failed', {
                    detail: {
                        reason: 'forbidden',
                        status: 403
                    }
                }));

                if (DEBUG_INTERCEPTORS) {
                    console.log('[apiClient] Dispatched auth-failed event for 403');
                }
            } else if (isPermissionEndpoint) {
                console.warn(`[apiClient] Permission denied for ${originalRequest.url}, not logging out user`);
            } else if (isExternalService) {
                console.warn(`[apiClient] External service access forbidden for ${originalRequest.url}, not logging out user`);
            } else if (DEBUG_INTERCEPTORS) {
                console.log('[apiClient] 403 received but redirect already in progress');
            }

            return Promise.reject(error);
        }
        
        // Handle 410 Gone - resource permanently deleted
        if (error.response?.status === 410) {
            console.warn('[apiClient] 410 Gone - Resource no longer exists:', originalRequest.url);

            // Mark error with metadata for query-level handling
            error.isGone = true;
            error.userMessage = 'This resource is no longer available.';

            // Don't retry - resource is permanently gone
            return Promise.reject(error);
        }

        // Handle 429 Rate Limit Exceeded
        if (error.response?.status === 429) {
            // Initialize retry count if not present
            if (!originalRequest._retryCount) {
                originalRequest._retryCount = 0;
            }

            // Maximum of 3 retries
            if (originalRequest._retryCount < 3) {
                originalRequest._retryCount += 1;

                // Get retry delay
                const retryAfter = error.response.headers['retry-after'];
                const delay = getRetryDelay(originalRequest._retryCount, retryAfter);

                // Log rate limit for monitoring
                console.warn(`[apiClient] Rate limit hit. Retrying in ${delay}ms (attempt ${originalRequest._retryCount}/3)`);

                if (DEBUG_INTERCEPTORS) {
                    console.log('[apiClient] Waiting before retry...', { delay, retryCount: originalRequest._retryCount });
                }

                // Wait before retrying
                await sleep(delay);

                if (DEBUG_INTERCEPTORS) {
                    console.log('[apiClient] Retrying request after rate limit delay');
                }

                // Retry the request
                return apiClient(originalRequest);
            } else {
                // Max retries exceeded, enhance error with user-friendly message
                const retryAfter = error.response.headers['retry-after'];
                const waitTime = retryAfter ? `${retryAfter} seconds` : 'a moment';

                error.isRateLimit = true;
                error.userMessage = `Too many requests. Please wait ${waitTime} before trying again.`;
                error.retryAfter = retryAfter;

                console.error('[apiClient] Rate limit exceeded after maximum retries');
            }
        }

        if (DEBUG_INTERCEPTORS && error.response) {
            console.log('[apiClient] Returning rejected promise for status:', error.response.status);
        }

        return Promise.reject(error);
    }
);

// Reset the redirect flag when auth is restored
export const resetAuthRedirectFlag = () => {
    if (DEBUG_INTERCEPTORS) {
        console.log('[apiClient] Manually resetting redirect flag');
    }
    isRedirecting = false;
    redirectTimestamp = null;
};

// Get interceptor status for debugging
export const getInterceptorStatus = () => {
    return {
        isRedirecting: checkRedirectFlag(),
        redirectTimestamp,
        requestInterceptorsCount: apiClient.interceptors.request.handlers.length,
        responseInterceptorsCount: apiClient.interceptors.response.handlers.length,
        debugMode: DEBUG_INTERCEPTORS
    };
};

// Health check function to verify interceptors are working
export const testInterceptors = async () => {
    console.log('[apiClient] Running interceptor health check...');

    const status = getInterceptorStatus();
    console.log('[apiClient] Interceptor status:', status);

    if (status.requestInterceptorsCount === 0) {
        console.error('[apiClient] WARNING: No request interceptors registered!');
        return false;
    }

    if (status.responseInterceptorsCount === 0) {
        console.error('[apiClient] WARNING: No response interceptors registered!');
        return false;
    }

    console.log('[apiClient] Interceptors appear to be registered correctly');
    return true;
};

export default apiClient;
